import type { RouteLocationRaw } from 'vue-router';
import type { SearchHit, SearchKind } from '@/features/search/search.api';

/**
 * What each kind is called, and where selecting one goes.
 *
 * Exhaustive by type: `Record<SearchKind, …>` means adding a kind to the contract without
 * teaching the client where it leads is a compile error rather than a result that does
 * nothing when clicked.
 */
interface KindPresentation {
  readonly label: string;
  readonly route: (hit: SearchHit) => RouteLocationRaw;
}

export const SEARCH_KINDS: Record<SearchKind, KindPresentation> = {
  employee: {
    label: 'Personas',
    route: (hit) => ({ name: 'employee-detail', params: { id: hit.id } }),
  },
  news: {
    label: 'Comunicados',
    route: (hit) => ({ name: 'news-detail', params: { id: hit.id } }),
  },
  document: {
    label: 'Políticas',
    route: (hit) => ({ name: 'document-detail', params: { id: hit.id } }),
  },
  'internal-benefit': {
    label: 'Beneficios internos',
    route: (hit) => ({ name: 'internal-benefit-detail', params: { id: hit.id } }),
  },
  'partner-offer': {
    label: 'Convenios',
    route: (hit) => ({ name: 'partner-offer-detail', params: { id: hit.id } }),
  },
  course: {
    label: 'Capacitación',
    route: (hit) => ({ name: 'course-detail', params: { id: hit.id } }),
  },
};

/** Where to see everything of a kind, when the search hit its own ceiling. */
export const KIND_FULL_LIST: Record<SearchKind, { label: string; route: RouteLocationRaw }> = {
  employee: { label: 'el Directorio', route: { name: 'employees' } },
  news: { label: 'Comunicados', route: { name: 'news' } },
  document: { label: 'Políticas', route: { name: 'documents' } },
  'internal-benefit': { label: 'Beneficios', route: { name: 'benefits' } },
  'partner-offer': { label: 'Beneficios', route: { name: 'benefits' } },
  course: { label: 'Capacitación', route: { name: 'training' } },
};

/** The order groups appear in. People first, because a name is what most searches are. */
export const KIND_ORDER: readonly SearchKind[] = [
  'employee',
  'news',
  'document',
  'internal-benefit',
  'partner-offer',
  'course',
];

export function groupHits(hits: readonly SearchHit[]): Array<{
  kind: SearchKind;
  label: string;
  hits: SearchHit[];
}> {
  return KIND_ORDER.map((kind) => ({
    kind,
    label: SEARCH_KINDS[kind].label,
    hits: hits.filter((hit) => hit.kind === kind),
  })).filter((group) => group.hits.length > 0);
}
